export enum MainPageEvents {
  /** Event when user expands or collapse user, companies or projects panel after search */
  setCollapsesPanel = "set collapses panel",

  /** Event when user clicks on an item from search results */
  openItem = "open item",

  /** Clear search in the home page */
  clearSearch = "clear search",

  /** Event when user starts typing in the search bar */
  startSearch = "start search",
}

export enum NavbarEvents {
  /** Trigger when user clicks on create company */
  createCompany = "create company",

  /** Event when user tries to fetch the newly created workspace plan after creating a new workspace */
  fetchNewlyCreatedWorkspacePlan = "fetch newly created workspace plan",

  /** Event when user tries to create a plan for the newly created workspace */
  createPlanForNewlyCreatedWorkspace = "create plan for newly created workspace",

  /** Event when user opens Sphere from the environment icon: the workspace list, or the open workspace or project */
  navigateToWorkspaceList = "navigate to workspace list",

  /** Event when user clicks on the user menu button in the navbar */
  openUserMenu = "open user menu",

  /** Event when user clicks on logout from the user menu */
  logout = "logout",
}

export enum GeneralProfileEvents {
  /** Event when user copies a properties to clipboard */
  copyToClipboard = "copy to clipboard",

  /** Event when user copies details to clipboard */
  copyDetailsToClipboard = "copy details to clipboard",
}

export enum GeneralTableEvents {
  /** Event when user exports the table data as CSV */
  exportAsCsv = "export as csv",

  /** Event when user exports the table data in a chosen file format */
  exportTable = "export table",

  /** Event when user clicks on apply filter in the table */
  applyFilter = "apply filter",

  /** Event when user clicks on clear filter in the table */
  clearFilters = "clear filters",

  /** Event when user changes the sorting in the table */
  changeSorting = "change sorting",

  /** Event when user clicks on a row in the table */
  clickRow = "click row",

  /** Event when user expands/collapses a row in the table */
  toggleRow = "toggle row",

  /** Event when user copies the id of an entity to clipboard from the table */
  copyRowIdToClipboard = "copy row id to clipboard",
}

export enum WorkspaceEvents {
  /** Event when user logs in as workspace viewer */
  loginAsWorkspaceViewer = "login as workspace viewer",

  /** Event when user views members of a workspace */
  viewMembers = "view members",

  /** Event when user opens edit workspace details */
  openEditWorkspaceDetails = "open edit workspace details",

  /** Event when user updates a workspace details */
  editWorkspaceDetails = "edit workspace details",

  /** Event when user request to create a project by navigating to the create project page */
  navigateToCreateProject = "navigate to create project",

  /** Event when user creates a project */
  createProject = "create project",

  /** Event when user opens invite member to workspace */
  openInviteMemberToWorkspace = "open invite member to workspace",

  /** Event when user invites a member to the workspace */
  inviteMember = "invite member to workspace",

  /** Event when user opens a company in the legacy admin panel */
  openCompanyInLegacyAdminPanel = "open company in legacy admin panel",

  /** Event when user deletes a workspace */
  deleteWorkspace = "delete workspace",

  /** Event when user resend an invitation to a workspace */
  resendInvitationToWorkspace = "resend invitation to workspace",
}

export enum ProjectEvents {
  /** Event when user opens clone project dialog */
  openCloneProjectDialog = "open clone project dialog",

  /** Event when user clones a project */
  cloneProject = "clone project",

  /** Event when user makes a project standalone */
  makeProjectStandalone = "make project standalone",

  /** Event when user opens a project in the legacy admin panel */
  openProjectInLegacyAdminPanel = "open project in legacy admin panel",

  /** Event when user opens edit project details */
  openEditProjectDetails = "open edit project details",

  /** Event when user updates a project details */
  editProjectDetails = "edit project details",
}

export enum PlanEvents {
  /** Event when user click on add plan */
  requestAddingPlan = "request adding plan",

  /** Event when user adds a plan */
  addPlan = "add plan",

  /** Event when user opens deactivate plan dialog */
  openDeactivatePlan = "open deactivate plan",

  /** Event when user deactivates a plan */
  deactivatePlan = "deactivate plan",

  /** Event when user deactivates a license */
  deactivateLicense = "deactivate license",

  /** Event when user opens the dialog to permanently delete a plan */
  openDeletePlan = "open delete plan",

  /** Event when user permanently deletes a plan */
  deletePlan = "delete plan",

  /** Event when user opens the dialog to deactivate or delete the selected plans at once */
  openBulkPlanAction = "open bulk plan action",

  /** Event when user deactivates or deletes the selected plans at once */
  applyBulkPlanAction = "apply bulk plan action",

  /** Event when user updates a plan comment */
  updatePlanComment = "update plan comment",

  /** Event when user opens the dialog to add SCENE seats to a plan */
  openAddSceneLicense = "open add scene license",

  /** Event when user adds SCENE seats to a plan */
  addSceneLicense = "add scene license",

  /** Event when some of the SCENE seats of a newly created plan could not be created */
  createSceneSeatsFailed = "create scene seats failed",

  /** Event when some of the token plans of a newly created plan could not be created */
  createTokenPlansFailed = "create token plans failed",

  /** Event when user opens the dialog to free an assigned SCENE seat */
  openUnassignSceneSeat = "open unassign scene seat",

  /** Event when user frees an assigned SCENE seat */
  unassignSceneSeat = "unassign scene seat",

  /** Event when user creates a new single feature */
  createFeature = "create feature",

  /** Event when user creates a new feature bundle */
  createBundle = "create bundle",

  /** Event when user opens the dialog to copy a plan */
  openCopyPlan = "open copy plan",

  /** Event when user continues from the dialog to the page that creates the copy of a plan */
  requestCopyingPlan = "request copying plan",

  /** Event when some of the SCENE seat assignments of a copied plan could not be moved to its copy */
  copySceneSeatAssignmentsFailed = "copy scene seat assignments failed",
}

export enum ExperimentalFeatureEvents {
  /** Event when user opens developer tools */
  openDevTools = "open dev tools",

  /** Event when user sets developer mode */
  setDevMode = "set dev mode",

  /** Event when user sets beta mode */
  setBetaMode = "set beta mode",

  /** Event when user sets show all plans */
  setShowAllPlans = "set show all plans",

  /** Event when user sets show all products */
  setShowAllProducts = "set show all products",

  /** Event when user sets show full date time */
  setShowFullDateTime = "set show full date time",

  /** Event when user sets show hardware license */
  setShowHardwareLicense = "set show hardware license",

  /** Event when user sets allow bulk operations */
  setAllowBulkOperations = "set allow bulk operations",

  /** Triggers the event when user toggles sending errors to Sentry */
  enableSentry = "enable Sentry",

  /** Event when user navigates to a page from the dev tools dialog */
  openDevPage = "open dev page",

  /** Event when user adds a feature to a bundle or removes it from a bundle */
  changeBundleFeature = "change bundle feature",
}

export enum OpenTabEvents {
  /** Event when user opens company profile tab */
  openCompanyProfileTabs = "open company profile tabs",

  /** Event when user opens project profile tab */
  openProjectProfileTabs = "open project profile tabs",

  /** Event when user opens member profile tab */
  openMemberProfileTabs = "open member profile tabs",
}

export enum MemberEvents {
  /** Event when user logs in (impersonation) as member */
  loginAsMember = "login as member",

  /** Event when user opens the generate reset password link dialog */
  openResetPasswordLinkDialog = "open reset password link dialog",

  /** Event when user requests a password reset link for a member */
  generateResetPasswordLink = "generate reset password link",

  /** Event when user opens delete member dialog */
  openDeleteMemberDialog = "open delete member dialog",

  /** Event when user progresses through delete member steps */
  deleteMemberStep = "delete member dialog step",

  /** Event when opens a dialog to delete a member from a workspace */
  openDeleteMemberFromWorkspaceDialog = "open delete member from workspace dialog",

  /** Event when user cancels deleting a member from a workspace */
  deleteMemberFromWorkspaceCancel = "delete member from workspace cancel",

  /** Event when user confirms deleting a member from a workspace */
  deleteMemberFromWorkspaceConfirm = "delete member from workspace confirm",

  /** Event when user deletes a member from a workspace */
  deleteMemberFromWorkspace = "delete member from workspace",

  /** Event when user deletes a member */
  deleteMember = "delete member",

  /** Event when user opens edit member details dialog */
  openEditMemberDetailsDialog = "open edit member details dialog",

  /** Event when user edits member details */
  editMemberDetails = "edit member details",

  /** Event when user opens a member in the legacy admin panel */
  openMemberInLegacyAdminPanel = "open member in legacy admin panel",

  /** Event when user opens change authentication provider dialog */
  openChangeAuthProviderDialog = "open change auth provider dialog",

  /** Event when user changes authentication provider */
  changeAuthProvider = "change auth provider",

  /** Event when user sends an email to a member */
  sendEmailToMember = "send email to member",

  /** Event when user opens revoke member sessions dialog */
  openRevokeMemberSessionsDialog = "open revoke member sessions dialog",

  /** Event when user revokes member sessions */
  revokeMemberSessions = "revoke member sessions",
}

export enum LinkWithQRCodeDialogEvents {
  copyLink = "copy link",
}

export enum SupportEvents {
  reportSceneLicenseIssue = "report scene license issue",
}
